import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    ArrowRightIcon,
    CoinsIcon,
    CalendarStarIcon,
    LightningIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

// Visual-only metadata, keyed by the package id the backend defines.
// Pricing / labels / CNY hints come from the backend — never hardcoded.
const PACKAGE_VISUALS = {
    credits10: {
        // Chinese-first bilingual labels
        title_cn: "10 张照片",
        title_en: "10 photos",
        sub_cn: "一次性付费 · 永不过期",
        sub_en: "One-time · never expires",
        Icon: CoinsIcon,
        accent: false,
    },
    sub100: {
        title_cn: "100 张照片/月",
        title_en: "100 photos / month",
        sub_cn: "每月订阅 · 性价比最高",
        sub_en: "Monthly subscription · best value",
        Icon: CalendarStarIcon,
        accent: true,
    },
};

export default function BuyCreditsCard({ source = "upload_page" }) {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/checkout/packages");
                if (cancelled) return;
                // Preserve backend ordering; filter to ids we know how to
                // render so an unknown future package doesn't crash the UI.
                const list = (data?.packages || []).filter(
                    (p) => PACKAGE_VISUALS[p.id],
                );
                setPackages(list);
            } catch (err) {
                if (cancelled) return;
                toast.error(
                    "套餐加载失败，请刷新页面 / Couldn't load packages.",
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const start = async (pkgId) => {
        if (busyId) return;
        setBusyId(pkgId);
        track("checkout_start", { source, package_id: pkgId });
        try {
            const { data } = await api.post("/checkout/create", {
                // Backend's design contract: send only the package id.
                // Backend resolves the Stripe Price from STRIPE_PRICE_*
                // env vars — frontend NEVER sends a price.
                package_id: pkgId,
                origin_url: window.location.origin,
            });
            if (data?.url) {
                window.location.href = data.url;
                return;
            }
            throw new Error("Missing checkout url.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.message ||
                "结账失败，请重试 / Couldn't start checkout. Try again.";
            toast.error(msg);
            track("checkout_error", {
                source,
                package_id: pkgId,
                message: msg,
            });
            setBusyId(null);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#1a1a1f] to-[#0d0d10] p-6 sm:p-8"
            data-testid="buy-credits-card"
        >
            <div className="space-y-2 mb-6">
                <h3 className="font-cn font-heading text-xl sm:text-2xl font-medium tracking-tight">
                    选择套餐 · Choose a package
                </h3>
                <p className="text-sm text-white/60 leading-relaxed font-cn">
                    支持 Apple Pay、Google Pay、支付宝、微信支付与信用卡 · Apple Pay,
                    Google Pay, Alipay, WeChat Pay & cards. Secure checkout by
                    Stripe.
                </p>
            </div>

            {loading ? (
                <div
                    data-testid="packages-loading"
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                    {[0, 1].map((i) => (
                        <div
                            key={i}
                            className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 h-44 animate-pulse"
                        />
                    ))}
                </div>
            ) : packages.length === 0 ? (
                <p
                    data-testid="packages-empty"
                    className="text-sm text-white/55 font-cn"
                >
                    暂时无法加载套餐，请稍后再试。/ Packages unavailable. Please
                    try again.
                </p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {packages.map((pkg) => {
                        const vis = PACKAGE_VISUALS[pkg.id];
                        const busy = busyId === pkg.id;
                        const otherBusy = busyId && busyId !== pkg.id;
                        const Icon = vis.Icon;
                        return (
                            <button
                                key={pkg.id}
                                type="button"
                                onClick={() => start(pkg.id)}
                                disabled={busy || otherBusy}
                                data-testid={`buy-pkg-${pkg.id}`}
                                className={[
                                    "group relative text-left rounded-2xl border p-5 transition-all",
                                    "flex flex-col gap-3",
                                    vis.accent
                                        ? "bg-white text-black border-white hover:bg-white/95"
                                        : "bg-white/[0.04] text-white border-white/10 hover:border-white/25 hover:bg-white/[0.06]",
                                    busy || otherBusy
                                        ? "opacity-60 cursor-not-allowed"
                                        : "active:scale-[0.99]",
                                ].join(" ")}
                            >
                                {vis.accent ? (
                                    <span
                                        data-testid={`buy-pkg-${pkg.id}-badge`}
                                        className="inline-flex items-center self-start gap-1 text-[10px] tracking-[0.18em] uppercase font-semibold bg-black text-white rounded-full px-2 py-0.5"
                                    >
                                        <LightningIcon
                                            size={10}
                                            weight="fill"
                                        />
                                        性价比最高 · Best value
                                    </span>
                                ) : null}
                                <div className="flex items-center gap-2">
                                    <span
                                        className={[
                                            "inline-flex w-9 h-9 items-center justify-center rounded-full",
                                            vis.accent
                                                ? "bg-black text-white"
                                                : "bg-white text-black",
                                        ].join(" ")}
                                    >
                                        <Icon size={18} weight="fill" />
                                    </span>
                                    <span
                                        className="font-cn font-heading text-base font-medium leading-tight"
                                        data-testid={`buy-pkg-${pkg.id}-title`}
                                    >
                                        {vis.title_cn} / {vis.title_en}
                                    </span>
                                </div>
                                <div>
                                    <div
                                        className="font-heading text-3xl font-medium tracking-tight tabular-nums"
                                        data-testid={`buy-pkg-${pkg.id}-price`}
                                    >
                                        {pkg.amount_display}
                                    </div>
                                    {pkg.amount_cny_hint ? (
                                        <div
                                            className={[
                                                "text-[11px] mt-0.5 font-cn",
                                                vis.accent
                                                    ? "text-black/55"
                                                    : "text-white/45",
                                            ].join(" ")}
                                            data-testid={`buy-pkg-${pkg.id}-cny`}
                                        >
                                            {pkg.amount_cny_hint}
                                        </div>
                                    ) : null}
                                    <div
                                        className={[
                                            "text-xs mt-1.5 font-cn leading-snug",
                                            vis.accent
                                                ? "text-black/60"
                                                : "text-white/50",
                                        ].join(" ")}
                                    >
                                        {vis.sub_cn} / {vis.sub_en}
                                    </div>
                                </div>
                                <span
                                    className={[
                                        "inline-flex items-center gap-1.5 text-xs font-medium mt-1 font-cn",
                                        vis.accent
                                            ? "text-black"
                                            : "text-white/80",
                                    ].join(" ")}
                                >
                                    {busy
                                        ? "跳转中… / Redirecting…"
                                        : "立即购买 / Choose"}
                                    {!busy ? (
                                        <ArrowRightIcon
                                            size={13}
                                            weight="bold"
                                            className="group-hover:translate-x-0.5 transition-transform"
                                        />
                                    ) : null}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </motion.div>
    );
}
